import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { useRecoilValue } from "recoil";
import { darkTheme, lightTheme } from 'styles/theme';
import { isDarkAtom } from 'styles/atoms';
import GlobalStyles from 'styles/global-styles';
import './styles/css/style.css';
import { AgentLayout, UserLayout, UserTheme, AdminLayout } from 'pages/Layout';
import ModalLayout from 'components/common/ModalLayout';

// 관리자 import
import Statistics from 'pages/admin/statistics/Statistics';
import AdminMain from 'pages/admin/Main';
import AdminLogin from 'pages/admin/Login';
import AdminUserList from 'pages/admin/user/List';
import AdminContractList from 'pages/admin/contract/List';
import AdminContractEdit from 'pages/admin/contract/Edit';
import AdminContractRegister from 'pages/admin/contract/Register';
import AdminAdminList from 'pages/admin/admin/List';
import AdminAdminRegister from 'pages/admin/admin/Register';
import AdminAdminEdit from 'pages/admin/admin/Edit';
import AdminAdminInfo from 'pages/admin/admin/Info';
import {AdminNoticeList, AdminNoticeDetail} from 'pages/admin/notice/Notice';
import AdminPopupUserEdit from 'pages/admin/popup/UserEdit';
import AdminPopupRecorderEdit from 'pages/admin/popup/RecorderEdit';
import AdminPopupRecorderReplace from 'pages/admin/popup/RecorderReplace';
import AdminPopupRecorderEvent from 'pages/admin/popup/RecorderEvent';
import AdminPopupViewerViewer from 'pages/admin/popup/Viewer';
import AdminPopupSubUserList from 'pages/admin/popup/SubUserList';
import AdminAfterServiceList from 'pages/admin/afterService/AfterServiceList';
import AdminAfterServiceProcess from 'pages/admin/afterService/AfterServiceProcess';
import AdminJisaList from 'pages/admin/jisa/List';
import AdminJisaProcess from 'pages/admin/jisa/Process';
import AdminRecorderModelList from 'pages/admin/recorderModel/List';
import AdminRecorderModelProcess from 'pages/admin/recorderModel/Process';
import AdminCameraModelList from 'pages/admin/cameraModel/List';
import AdminCameraModelProcess from 'pages/admin/cameraModel/Process';
import AdminError from 'pages/admin/error/Error';
import AdminHappyCallSend from 'pages/admin/happyCall/Send';
import AdminHappyCallTemplateList from 'pages/admin/happyCall/TemplateList';
import AdminHappyCallTemplateProcess from 'pages/admin/happyCall/TemplateProcess';

// 매니저 import
import AgentSearchId from 'pages/agent/recorder/SearchUser';
import AgentRegister from 'pages/agent/recorder/Register';
import AgentEdit from 'pages/agent/recorder/Edit';
import AgentReplace from 'pages/agent/recorder/Replace';
import AgentRecorderList from 'pages/agent/recorder/List';
import AgentManagement from 'pages/agent/contract/Management';
import AgentContractRegister from 'pages/agent/contract/Register';
import AgentContractEdit from 'pages/agent/contract/Edit';
import AgentUserRegister from 'pages/agent/user/Register';
import AgentMenu from 'pages/agent/Menu';
import AgentManagerOtpRegister from 'pages/agent/otp/Register';

// 고객 import
import UserLogin from 'pages/user/UserLogin';
import {UserAgree, UserFirstPwChange, UserVerification} from 'pages/user/UserInit';
import { BadRequestUserWebView, NotFound, NotFoundUserWebView } from 'pages/PageFail';
import { UserNoticeDetail, UserNoticeList } from 'pages/user/UserNotice';
import { UserFindId, UserFindPw } from 'pages/user/UserFind';
import { AppBridge } from 'pages/test/AppBridge';
import { UserAfterService, UserAfterServiceCreate, UserAfterServiceDetail } from 'pages/user/UserAfterService';
import { NiceUi } from 'components/nice/NiceUi';
import { UserInfoEdit } from 'pages/user/UserInfo';
import SubList from 'pages/user/sub/List';
import SubProcess from 'pages/user/sub/Process';
import FirstLogin from 'pages/user/sub/FirstLogin';

// Event 상세 설명
import EventDetail from 'pages/Event/Detail';
// Store 안내 페이지
import StoreGuide from 'pages/Info/StoreGuide';

function App() {
  const isDark = useRecoilValue(isDarkAtom);

  return (
    <ThemeProvider theme={isDark ? darkTheme : lightTheme}>
      <GlobalStyles />
      <BrowserRouter>
        <Routes >
          <Route element={<ModalLayout />}>
            {/* /agent 경로가 올바르게 설정되어 있는지 확인 */}
            <Route path="/agent">
              {/* /agent 경로로만 접근했을 때 /admin/login으로 리다이렉트 */}
              <Route index element={<Navigate to="/agent/login" replace />} />
              <Route path="login" element={<AdminLogin />} />
              <Route element={<AgentLayout />}>
                <Route path="recorder">
                  <Route path="list/:userId" element={<AgentRecorderList />} />
                  <Route path="register/:userId/:contractNo?/:mac?/:recorderIdx?" element={<AgentRegister />} />
                  <Route path="edit/:recorderIdx" element={<AgentEdit />} />
                  <Route path="replace/:recorderBefIdx/:contractNo?/:mac?/:recorderIdx?" element={<AgentReplace />} />
                </Route>
                <Route path="contract">
                  <Route path="register" element={<AgentContractRegister />} />
                  <Route path="edit/:contractIdx" element={<AgentContractEdit />} />
                  <Route path="management" element={<AgentManagement />} />
                </Route>
                <Route path="user">
                  <Route path="register" element={<AgentUserRegister />} />
                </Route>
                <Route path="menu" element={<AgentMenu />} />
                <Route path="search/user" element={<AgentSearchId />} />
                <Route path="manager-otp/register" element={<AgentManagerOtpRegister />} />
              </Route>
            </Route>

            {/* /admin 경로가 올바르게 설정되어 있는지 확인 */}
            <Route path="/admin">
              {/* /admin 경로로만 접근했을 때 /admin/login으로 리다이렉트 */}
              <Route index element={<Navigate to="/admin/login" replace />} />
              {/* 로그인이 안되어 있을 때(권한이 없을 때) 로그인 페이지로 이동시켜야 함 */}
              <Route path="login" element={<AdminLogin />} />
              <Route element={<AdminLayout />}>
                <Route index element={<AdminMain />} />
                <Route path="user" element={<Statistics />}>
                  <Route path="list" element={<AdminUserList />} />
                </Route>
                <Route path="contract" element={<Statistics />}>
                  <Route path="list" element={<AdminContractList />} />
                  <Route path="register" element={<AdminContractRegister />} />
                  <Route path="edit/:contractNo" element={<AdminContractEdit />} />
                </Route>
                <Route path="notice" element={<Statistics />}>
                  <Route path="list" element={<AdminNoticeList />} />
                  <Route path="detail/:noticeIdx" element={<AdminNoticeDetail />} />
                </Route>
                <Route path="mgmt" element={<Statistics />}>
                  <Route path="list" element={<AdminAdminList />} />
                  <Route path="register" element={<AdminAdminRegister />} />
                  <Route path="edit/:adminId" element={<AdminAdminEdit />} />
                  <Route path="my-info" element={<AdminAdminInfo />} />
                </Route>
                <Route path="after-service" element={<Statistics />}>
                  <Route index element={<AdminAfterServiceList />} />
                  <Route path="process/:asIdx" element={<AdminAfterServiceProcess />} />
                </Route>
                <Route path="jisa" element={<Statistics />}>
                  <Route index element={<AdminJisaList />} />
                  <Route path="process/:jisaIdx?" element={<AdminJisaProcess />} />
                </Route>
                <Route path="model" element={<Statistics />}>
                  <Route path="recorder" element={<AdminRecorderModelList />} />
                  <Route path="recorder/process/:recorderModelIdx?" element={<AdminRecorderModelProcess />} />
                  <Route path="camera" element={<AdminCameraModelList />} />
                  <Route path="camera/process/:cameraModelIdx?" element={<AdminCameraModelProcess />} />
                </Route>
                <Route path="happy-call" element={<Statistics />}>
                  <Route path="send" element={<AdminHappyCallSend />} />
                  <Route path="template-list" element={<AdminHappyCallTemplateList />} />
                  <Route path="template-process/:happyCallTemplateIdx?" element={<AdminHappyCallTemplateProcess />} />
                </Route>
              </Route>
              <Route path="popup">
                <Route path="user/edit/:userId" element={<AdminPopupUserEdit />} />
                <Route path="recorder/edit/:recorderIdx" element={<AdminPopupRecorderEdit />} />
                <Route path="recorder/replace/:recorderIdx" element={<AdminPopupRecorderReplace />} />
                <Route path="recorder/event/:recorderMac" element={<AdminPopupRecorderEvent />} />
                <Route path="viewer" element={<AdminPopupViewerViewer />} />
                <Route path="user/sub/list/:userId" element={<AdminPopupSubUserList />} />
              </Route>
              <Route path='*' element={<AdminError />} />
            </Route>

            {/* /user 경로가 올바르게 설정되어 있는지 확인 */}
            <Route path="/user">
              {/* 테스트용 로그인 화면 */}
              <Route path="login" element={<UserLogin />} /> 
              <Route element={<UserTheme />}>
                <Route path="find-id" element={<UserFindId />} />
                <Route path="find-pw" element={<UserFindPw />} />
              </Route>
              <Route element={<UserLayout />}>
                <Route path="init">
                  <Route path="verification" element={<UserVerification />} />
                  <Route path="agree" element={<UserAgree />} />
                  <Route path="first-pw-change" element={<UserFirstPwChange />} />
                </Route>

                <Route path="notice">
                  <Route path="list" element={<UserNoticeList />} />
                  <Route path="detail/:noticeIdx" element={<UserNoticeDetail />} />
                </Route>

                <Route path="after-service">
                  <Route path="list" element={<UserAfterService />} />
                  <Route path="create" element={<UserAfterServiceCreate />} />
                  <Route path="detail/:serviceIdx" element={<UserAfterServiceDetail />} />
                </Route>

                <Route path="info">
                  <Route path="edit" element={<UserInfoEdit />} />
                </Route>

                <Route path="sub">
                  <Route path="list" element={<SubList />} />
                  <Route path="process/:subUserIdx?" element={<SubProcess />} />
                  <Route path="first-pw-change" element={<FirstLogin />} />
                </Route>
              </Route>
              <Route element={<UserTheme />}>
                {/* User 400 페이지 */}
                <Route path="bad-request" element={<BadRequestUserWebView />} />
                {/* User 404 페이지 */}
                <Route path="*" element={<NotFoundUserWebView />} />
              </Route>
            </Route>

            <Route path="/event" element={<UserTheme />}>
              <Route path="detail" element={<EventDetail />} />
            </Route>

            <Route path="/focusview" element={<UserTheme />}>
              <Route index element={<StoreGuide />} />
            </Route>

            <Route element={<UserTheme />}>
              <Route path="/test">
                <Route path="bridge" element={<AppBridge />} />
                <Route path="nice" element={<NiceUi />} />
              </Route>
            </Route>

            {/* 404 페이지 */}
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
